import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { Repayment } from '@/domain/repayments'

export type OperationsRepaymentColumn =
  | 'created-date'
  | 'repayment-date'
  | 'payment-amount'
  | 'repayment-name'
  | 'data-source'
  | 'cash-advance'
  | 'created-by'
  | 'terminal'
  | 'customer-number'
  | 'customer-status'
  | 'cash-advance-number'
  | 'customer-name'
  | 'description'
  | 'comments'

export interface OperationsRepaymentTableLayout {
  ariaLabel: string
  emptyMessage: string
  columns: Array<{ label: string; value: OperationsRepaymentColumn }>
}

@Component({
  selector: 'app-operations-repayment-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './gocardless-repayment-table.component.html',
  styleUrl: './gocardless-repayment-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationsRepaymentTableComponent {
  @Input({ required: true }) repayments: Repayment[] = []
  @Input() totalRecords = 0
  @Input() totalPaymentAmount = 0
  @Input() loading = false
  @Input() loadingMore = false
  @Input() hasMore = false
  @Input({ required: true }) layout!: OperationsRepaymentTableLayout
  @Output() loadMore = new EventEmitter<void>()

  private readonly currencyFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  private readonly dateFormatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

  onScroll(event: Event): void {
    const container = event.currentTarget as HTMLElement
    if (
      this.hasMore
      && !this.loading
      && !this.loadingMore
      && container.scrollTop + container.clientHeight >= container.scrollHeight - 120
    ) {
      this.loadMore.emit()
    }
  }

  formatDate(value: string | undefined): string {
    if (!value) return '—'
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return value
    return this.dateFormatter.format(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  }

  formatAmount(value: number | string | undefined): string {
    const amount = Number(value)
    return this.currencyFormatter.format(Number.isFinite(amount) ? amount : 0)
  }

  relatedName(value: Repayment['cash_advances'] | Repayment['terminals'] | Repayment['merchants']): string {
    return value?.name || '—'
  }

  valueFor(repayment: Repayment, column: OperationsRepaymentColumn): string {
    switch (column) {
      case 'created-date': return this.formatDate(repayment.created_date)
      case 'repayment-date': return this.formatDate(repayment.repayment_date)
      case 'payment-amount': return this.formatAmount(repayment.payment_amount)
      case 'repayment-name': return repayment.description || '—'
      case 'data-source': return repayment.data_source || '—'
      case 'cash-advance':
      case 'cash-advance': return this.relatedName(repayment.cash_advances)
      case 'customer-number': return this.relatedName(repayment.cash_advances).replace(/\s*-\d+$/, '')
      case 'cash-advance-number': return this.relatedName(repayment.cash_advances)
      case 'customer-name': return this.relatedName(repayment.merchants)
      case 'description': return repayment.description || '—'
      case 'comments': return repayment.comments || '—'
      case 'created-by': return repayment.updated_by || '—'
      case 'terminal': return repayment.terminal_name || this.relatedName(repayment.terminals)
      case 'customer-status':
        return repayment.cash_advances?.active_cash_advance === true
          ? 'Active'
          : repayment.cash_advances?.active_cash_advance === false
            ? 'Inactive'
            : '—'
    }
  }
}
